"use strict";
/**
 * 套餐、配额与订阅状态机。
 *
 * 三条定价纪律落到代码里的样子：
 *   1) 配额是"软限 + 硬限"双阈值，超软限只提醒、超硬限才阻断写入——一次性卡死会直接激怒客户；
 *   2) 降级不是"断服务"，而是按矩阵逐级收缩，域名始终不释放；
 *   3) 所有状态流转都必须是显式表，禁止 if/else 里散落判断。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DOWNGRADE_MATRIX = exports.SUBSCRIPTION_TRANSITIONS = exports.SUBSCRIPTION_STATUSES = exports.ORDER_KINDS = exports.PAYMENT_PROVIDER_LABEL = exports.SCAN_PAYMENT_PROVIDERS = exports.PAYMENT_PROVIDERS = exports.ORDER_STATUSES = exports.BILLING_CYCLES = exports.PLAN_CATALOG = exports.PLAN_CODES = void 0;
exports.isBillingCycle = isBillingCycle;
exports.planPriceCents = planPriceCents;
exports.cycleDays = cycleDays;
exports.isScanPaymentProvider = isScanPaymentProvider;
exports.quotaLevel = quotaLevel;
exports.canConsume = canConsume;
exports.canTransitionSubscription = canTransitionSubscription;
exports.PLAN_CODES = ['starter', 'growth', 'business'];
exports.PLAN_CATALOG = {
    starter: {
        code: 'starter',
        name: { zh: '启航版', en: 'Starter' },
        priceCents: 12900,
        annualPriceCents: 123800,
        currency: 'CNY',
        billingPeriod: 'month',
        trialDays: 14,
        quotas: { sites: 1, products: 200, storage_mb: 2048, bandwidth_gb: 50, locales: 2, seats: 2 },
    },
    growth: {
        code: 'growth',
        name: { zh: '成长版', en: 'Growth' },
        priceCents: 32900,
        annualPriceCents: 315800,
        currency: 'CNY',
        billingPeriod: 'month',
        trialDays: 14,
        quotas: { sites: 1, products: 2000, storage_mb: 10240, bandwidth_gb: 200, locales: 4, seats: 5 },
    },
    business: {
        code: 'business',
        name: { zh: '企业版', en: 'Business' },
        priceCents: 89900,
        annualPriceCents: 863000,
        currency: 'CNY',
        billingPeriod: 'month',
        trialDays: 14,
        quotas: { sites: 3, products: 20000, storage_mb: 51200, bandwidth_gb: 800, locales: 4, seats: 20 },
    },
};
/** 计费周期。年付不是另一个套餐，它只是同一档的不同时长 */
exports.BILLING_CYCLES = ['monthly', 'annual'];
function isBillingCycle(v) {
    return typeof v === 'string' && exports.BILLING_CYCLES.includes(v);
}
/** 某个周期该收多少钱（分）。价格只从套餐目录取，调用方不许自己乘 */
function planPriceCents(plan, cycle) {
    return cycle === 'annual' ? plan.annualPriceCents : plan.priceCents;
}
/** 周期长度（天）。年付按 365 天算，付款周期与自然月对齐不现实 */
function cycleDays(cycle) {
    return cycle === 'annual' ? 365 : 30;
}
exports.ORDER_STATUSES = ['pending', 'paid', 'canceled', 'expired', 'refunded'];
exports.PAYMENT_PROVIDERS = ['wechat', 'alipay', 'manual'];
/**
 * 能扫码自助付款的通道。`manual` 不在其中 —— 它是"我们人工核对到账"，
 * 没有二维码也没有轮询，前端要换一套话术。
 */
exports.SCAN_PAYMENT_PROVIDERS = ['wechat', 'alipay'];
function isScanPaymentProvider(v) {
    return typeof v === 'string' && exports.SCAN_PAYMENT_PROVIDERS.includes(v);
}
/** 通道的中文名。写在这里而不是各页面各写一遍，否则订单列表和付款页会叫法不一 */
exports.PAYMENT_PROVIDER_LABEL = {
    wechat: '微信支付',
    alipay: '支付宝',
    manual: '对公转账',
};
/** new = 首次付费，renew = 同档续费，upgrade = 升档补差 */
exports.ORDER_KINDS = ['new', 'renew', 'upgrade'];
/**
 * 水位判定。80% 提醒、100% 软限、120% 硬限。
 * 软限之后只读、仍可导出数据；硬限才拒绝写入。留一条"能导出"的路，是避免客诉升级的最小成本。
 */
function quotaLevel(used, limit) {
    if (limit <= 0)
        return 'hard_exceeded';
    const ratio = used / limit;
    if (ratio >= 1.2)
        return 'hard_exceeded';
    if (ratio >= 1)
        return 'soft_exceeded';
    if (ratio >= 0.8)
        return 'warning';
    return 'ok';
}
function canConsume(level) {
    return level === 'ok' || level === 'warning';
}
exports.SUBSCRIPTION_STATUSES = [
    'trialing',
    'active',
    'past_due',
    'suspended',
    'canceled',
    'terminated',
];
exports.SUBSCRIPTION_TRANSITIONS = {
    trialing: ['active', 'canceled', 'terminated'],
    active: ['past_due', 'canceled', 'suspended'],
    past_due: ['active', 'suspended', 'canceled'],
    suspended: ['active', 'terminated'],
    canceled: ['active', 'terminated'],
    terminated: [],
};
function canTransitionSubscription(from, to) {
    return exports.SUBSCRIPTION_TRANSITIONS[from].includes(to);
}
exports.DOWNGRADE_MATRIX = {
    trialing: {
        status: 'trialing',
        graceDays: 0,
        siteReadable: true,
        siteWritable: true,
        adminAccess: true,
        domainRetained: true,
        dataRetentionDays: null,
        notify: { zh: '试用中，到期前 3 天提醒', en: 'Trial active; reminder 3 days before expiry' },
    },
    active: {
        status: 'active',
        graceDays: 0,
        siteReadable: true,
        siteWritable: true,
        adminAccess: true,
        domainRetained: true,
        dataRetentionDays: null,
        notify: { zh: '正常', en: 'Active' },
    },
    past_due: {
        status: 'past_due',
        graceDays: 7,
        siteReadable: true,
        siteWritable: true,
        adminAccess: true,
        domainRetained: true,
        dataRetentionDays: null,
        notify: { zh: '扣款失败，7 天宽限期内可正常使用', en: 'Payment failed; 7-day grace period' },
    },
    suspended: {
        status: 'suspended',
        graceDays: 30,
        /**
         * 停站，不是只读。
         *
         * 最初这里写的是 `siteReadable: true`（"客户网站不能直接消失"），
         * 但试用到期如果站点还能正常打开，客户就没有任何理由付钱 ——
         * 免费白用一个月，代价只是控制台里少一个按钮。
         * 现在的选择是：**到期即停站，数据保留 30 天，续费立刻恢复**。
         * 前台 404 而不是挂一个"该站已到期"的页面：挂告示等于替客户向他的
         * 海外买家宣布"这家公司欠费了"，那是不该由平台替他做的决定。
         */
        siteReadable: false,
        siteWritable: false,
        adminAccess: true,
        domainRetained: true,
        dataRetentionDays: 30,
        notify: { zh: '已停站：站点暂不可访问，数据保留 30 天', en: 'Site offline; data retained for 30 days' },
    },
    canceled: {
        status: 'canceled',
        graceDays: 0,
        // 试用到期直接落在这里（客户从没付过钱），对外同样应该是 404
        siteReadable: false,
        siteWritable: false,
        adminAccess: true,
        domainRetained: true,
        dataRetentionDays: 30,
        notify: { zh: '已取消，数据保留 30 天', en: 'Canceled; data retained for 30 days' },
    },
    terminated: {
        status: 'terminated',
        graceDays: 0,
        siteReadable: false,
        siteWritable: false,
        adminAccess: true,
        domainRetained: false,
        dataRetentionDays: 30,
        notify: { zh: '已终止，站点下线，30 天后归档数据', en: 'Terminated; site offline, data archived in 30 days' },
    },
};
//# sourceMappingURL=billing.js.map